import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { apiRequest } from "@/api/client";
import { useAuthStore } from "@/auth/authStore";
import type { AuthSessionContext, TokenPayload } from "@/auth/sessionTypes";
import { useLanguage } from "@/i18n/LanguageContext";
import { registerParticipant, ApiError } from "./api";

function buildSession(payload: TokenPayload): AuthSessionContext {
  return (
    payload.session ?? {
      user: payload.user,
      global_roles: [],
      member_profile: null,
      journal_memberships: [],
    }
  );
}

export function ConferenceRegisterPage() {
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const setTokens = useAuthStore((s) => s.setTokens);
  const setSessionContext = useAuthStore((s) => s.setSessionContext);
  const markHydrated = useAuthStore((s) => s.markHydrated);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [successCode, setSuccessCode] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const fd = new FormData(e.currentTarget);
    const payload = {
      email: String(fd.get("email") || ""),
      password: String(fd.get("password") || ""),
      password_confirm: String(fd.get("password_confirm") || ""),
      full_name: String(fd.get("full_name") || ""),
      phone: String(fd.get("phone") || ""),
      country: String(fd.get("country") || ""),
      city: String(fd.get("city") || "") || null,
      institution: String(fd.get("institution") || ""),
      title_or_role: String(fd.get("title_or_role") || ""),
      preferred_language: String(fd.get("preferred_language") || locale),
      organization: String(fd.get("organization") || "") || null,
      notes: String(fd.get("notes") || "") || null,
      passi_retrieval_code: String(fd.get("passi_retrieval_code") || "").trim() || null,
    };
    try {
      const res = await registerParticipant(payload);
      const access = res.data.access_token;
      const refresh = res.data.refresh_token || access;
      if (!access) throw new Error("No access token");
      setTokens({ accessToken: access, refreshToken: refresh });
      try {
        const refreshed = await apiRequest<{ data: TokenPayload }>("/v1/auth/refresh", {
          method: "POST",
          body: { refresh_token: refresh },
          anonymous: true,
        });
        setTokens({
          accessToken: refreshed.data.access_token,
          refreshToken: refreshed.data.refresh_token,
        });
        setSessionContext(buildSession(refreshed.data));
      } catch {
        setSessionContext({
          user: {
            id: res.data.registration.user_id,
            email: payload.email,
            full_name: payload.full_name,
            is_active: true,
            created_at: new Date().toISOString(),
          },
          global_roles: [],
          member_profile: null,
          journal_memberships: [],
        });
      }
      markHydrated();
      setSuccessCode(res.data.registration.registration_code);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.common.error);
    } finally {
      setSubmitting(false);
    }
  };

  if (successCode) {
    return (
      <div className="dn-card" style={{ maxWidth: 560 }}>
        <h1>{t.register.success}</h1>
        <p>{t.register.successBody}</p>
        <p>
          <strong>{t.register.code}:</strong> {successCode}
        </p>
        <p className="dn-muted">{t.register.cardAtVenueNote}</p>
        <div className="dn-cta-row">
          <button type="button" className="dn-btn dn-btn-gold" onClick={() => navigate("/dashboard")}>
            {t.register.goDashboard}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ fontFamily: "var(--dn-font-display)", color: "var(--dn-green-900)" }}>
        {t.register.title}
      </h1>
      <p className="lead">{t.register.subtitle}</p>
      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      <form className="dn-form" onSubmit={onSubmit}>
        <label>
          {t.register.email}
          <input name="email" type="email" required />
        </label>
        <label>
          {t.register.password}
          <input name="password" type="password" minLength={8} required />
        </label>
        <label>
          {t.register.confirm}
          <input name="password_confirm" type="password" minLength={8} required />
        </label>
        <label>
          {t.register.fullName}
          <input name="full_name" required />
        </label>
        <label>
          {t.register.phone}
          <input name="phone" required />
        </label>
        <label>
          {t.register.country}
          <input name="country" defaultValue="Nigeria" required />
        </label>
        <label>
          {t.register.city}
          <input name="city" />
        </label>
        <label>
          {t.register.institution}
          <input name="institution" required />
        </label>
        <label>
          {t.register.role}
          <input name="title_or_role" required />
        </label>
        <label>
          {t.register.preferredLang}
          <select name="preferred_language" defaultValue={locale}>
            <option value="en">{t.papers.langEnglish}</option>
            <option value="ar">{t.papers.langArabic}</option>
            <option value="fr">{t.papers.langFrench}</option>
          </select>
        </label>
        <label>
          {t.register.organization}
          <input name="organization" />
        </label>
        <label>
          {t.register.notes}
          <textarea name="notes" />
        </label>

        <fieldset className="dn-passi-block">
          <legend>{t.register.passiCode}</legend>
          <div className="dn-passi-row">
            <input
              name="passi_retrieval_code"
              maxLength={16}
              autoComplete="off"
              spellCheck={false}
              placeholder="ABC123"
              aria-label={t.register.passiCode}
              style={{ textTransform: "uppercase", letterSpacing: "0.08em" }}
            />
            <a
              className="dn-btn dn-btn-ghost"
              href="https://passi.fingletek.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.register.passiCreateLink}
            </a>
          </div>
          <p className="dn-passi-optional">{t.register.passiOptionalNote}</p>
          <details className="dn-passi-help">
            <summary>{t.register.passiInstructionsTitle}</summary>
            <ol className="dn-passi-summary">
              {t.register.passiSummarySteps.split("\n").map((line) => (
                <li key={line}>{line.replace(/^\d+[.)]\s*/, "").replace(/^[٠-٩]+[.)]\s*/, "")}</li>
              ))}
            </ol>
            <a
              className="dn-btn dn-btn-primary dn-passi-guide-link"
              href="/passi-guide"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.register.passiOpenGuide}
            </a>
            <p className="dn-passi-newtab-note">{t.register.passiGuideOpensNewTab}</p>
          </details>
        </fieldset>

        <button className="dn-btn dn-btn-primary" disabled={submitting} type="submit">
          {t.register.submit}
        </button>
      </form>
      <p style={{ marginTop: "1rem" }}>
        <Link to="/login">{t.nav.login}</Link>
      </p>
    </div>
  );
}
